import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Button } from 'shared/ui/button'
import { Label } from 'shared/ui/label'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Lock, CheckCircle, AlertTriangle, ArrowLeft, UserCheck, ShieldCheck } from 'lucide-react'
import { login, LoginResponseDto } from 'modules/auth/api/authService'
import { isAzureConfigured, startAzureLogin } from 'modules/auth/api/azureAuth'

interface EmployeeLoginProps {
  onLogin: (userData: any) => void
  onBack: () => void
}

export function EmployeeLogin({ onLogin, onBack }: EmployeeLoginProps) {
  const [loginData, setLoginData] = useState({ employeeId: '', password: '' })
  const [isLoading, setIsLoading] = useState(false)
  const [isAzureLoading, setIsAzureLoading] = useState(false)
  const [isAzureVisible, setIsAzureVisible] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let isMounted = true

    const loadAzureVisibility = async () => {
      try {
        const configured = await isAzureConfigured('EMPLOYEE')
        if (isMounted) {
          setIsAzureVisible(configured)
        }
      } catch {
        if (isMounted) {
          setIsAzureVisible(false)
        }
      }
    }

    loadAzureVisibility()

    return () => {
      isMounted = false
    }
  }, [])

  const handleInputChange = (field: keyof typeof loginData, value: string) => {
    setLoginData((prev) => ({ ...prev, [field]: value }))
    setError('')
  }

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response: LoginResponseDto = await login({
        username: loginData.employeeId.trim(),
        password: loginData.password,
        userType: 'EMPLOYEE',
      })
      onLogin({ ...response, userType: 'employee' })
    } catch (err: any) {
      console.error(err)
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        setError('\u0e0a\u0e37\u0e48\u0e2d\u0e1c\u0e39\u0e49\u0e43\u0e0a\u0e49\u0e2b\u0e23\u0e37\u0e2d\u0e23\u0e2b\u0e31\u0e2a\u0e1c\u0e48\u0e32\u0e19\u0e44\u0e21\u0e48\u0e16\u0e39\u0e01\u0e15\u0e49\u0e2d\u0e07')
      } else if (status === 423) {
        setError('\u0e1a\u0e31\u0e0d\u0e0a\u0e35\u0e1c\u0e39\u0e49\u0e43\u0e0a\u0e49\u0e16\u0e39\u0e01\u0e23\u0e30\u0e07\u0e31\u0e1a \u0e01\u0e23\u0e38\u0e13\u0e32\u0e15\u0e34\u0e14\u0e15\u0e48\u0e2d\u0e1c\u0e39\u0e49\u0e14\u0e39\u0e41\u0e25\u0e23\u0e30\u0e1a\u0e1a')
      } else {
        setError('\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e40\u0e02\u0e49\u0e32\u0e2a\u0e39\u0e48\u0e23\u0e30\u0e1a\u0e1a\u0e44\u0e14\u0e49\u0e43\u0e19\u0e02\u0e13\u0e30\u0e19\u0e35\u0e49 \u0e01\u0e23\u0e38\u0e13\u0e32\u0e25\u0e2d\u0e07\u0e43\u0e2b\u0e21\u0e48\u0e2d\u0e35\u0e01\u0e04\u0e23\u0e31\u0e49\u0e07')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleAzureLogin = async () => {
    setError('')
    if (!(await isAzureConfigured('EMPLOYEE'))) {
      setIsAzureVisible(false)
      setError('\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e01\u0e32\u0e23\u0e15\u0e31\u0e49\u0e07\u0e04\u0e48\u0e32 Azure \u0e01\u0e23\u0e38\u0e13\u0e32\u0e15\u0e34\u0e14\u0e15\u0e48\u0e2d\u0e1c\u0e39\u0e49\u0e14\u0e39\u0e41\u0e25\u0e23\u0e30\u0e1a\u0e1a')
      return
    }
    setIsAzureLoading(true)
    try {
      await startAzureLogin('EMPLOYEE')
    } catch (err: any) {
      console.error(err)
      const data = err?.response?.data
      const message =
        (typeof data === 'string' ? data : null) ??
        (typeof data?.message === 'string' ? data.message : null) ??
        err?.message ??
        '\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e40\u0e02\u0e49\u0e32\u0e2a\u0e39\u0e48\u0e23\u0e30\u0e1a\u0e1a\u0e14\u0e49\u0e27\u0e22 Azure \u0e44\u0e14\u0e49'
      setError(message)
      setIsAzureLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-4">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 self-start"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login Selection
        </Button>

        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3">
            <div className="p-3 bg-blue-500 rounded-full shadow-lg">
              <UserCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-blue-900">Employee Portal</h1>
          </div>
        </div>

        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur w-full">
          <CardHeader className="pb-4 text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl text-blue-900">
              <Lock className="w-6 h-6 text-blue-500" />
              Employee Sign In
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employeeId">Username</Label>
                <Input
                  id="employeeId"
                  type="text"
                  placeholder="Enter Username"
                  value={loginData.employeeId}
                  onChange={(event) => handleInputChange('employeeId', event.target.value)}
                  className="bg-blue-50/50 border-blue-200 focus:border-blue-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={(event) => handleInputChange('password', event.target.value)}
                  className="bg-blue-50/50 border-blue-200 focus:border-blue-400"
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Sign In to E-PTW
                  </>
                )}
              </Button>
            </form>

            {isAzureVisible && (
              <>
                <div className="flex items-center gap-3 text-xs text-blue-800/70">
                  <div className="h-px flex-1 bg-blue-100" />
                  <span>or sign in with</span>
                  <div className="h-px flex-1 bg-blue-100" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={handleAzureLogin}
                  disabled={isAzureLoading}
                >
                  {isAzureLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2" />
                      Redirecting to Azure...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2 text-blue-500" />
                      Sign In with Azure AD
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
